/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import sanitizeHtml from "sanitize-html";

import {
  parseISO,
  format,
  isPast,
  isToday,
  isFuture,
  sub,
  addDays,
  isBefore,
  setHours,
  setMinutes,
  setSeconds,
  startOfWeek,
} from "date-fns";
import { tr } from "date-fns/locale";
import { codeToHtml } from "shiki";
import { jwtDecode } from "jwt-decode";
import {
  IAssignedTask,
  IAssignedUnit,
  IAssignedProject,
  DateParts,
  IAiDialogHistoryItem,
  IPracticeHistoryItem,
} from "../types/generalTypes";
import { lsUserKey } from "./static";
import { IUser } from "../contexts/AuthContextProvider";
import { refreshTokenFn } from "../api/auth";
import { SandpackFiles } from "@codesandbox/sandpack-react";
import { isDeactivatedError, setDeactivatedEmail } from "./deactivated";

export function groupTasksByDay(
  tasks: IAssignedTask[]
): Map<string, IAssignedTask[]> {
  const result = new Map<string, IAssignedTask[]>();

  for (let i = 0; i < tasks.length; i++) {
    const item = tasks[i];
    const dateField =
      (item as IAssignedUnit).planning_date ||
      (item as IAssignedProject).planning_date;
    const date = parseISO(dateField);
    const key = format(date, "yyyy-MM-dd");

    result.set(key, [...(result.get(key) || []), item]);
  }

  return result;
}

/**
 * Bir tarih değerini işleyerek formatlanmış tarih parçalarını döndüren yardımcı fonksiyon
 * @param dateInput ISO formatında tarih string'i veya Date nesnesi
 * @returns Formatlanmış tarih parçaları
 */
export function getDateParts(dateInput: string | Date): DateParts {
  const date =
    typeof dateInput === "string"
      ? dateInput.includes("T")
        ? parseISO(dateInput)
        : new Date(dateInput)
      : dateInput;

  return {
    formattedDate: format(date, "d MMMM EE", { locale: tr }),
    formattedDateHourless: format(date, "d MMMM yyyy", { locale: tr }),
    formattedDateShortHourless: format(date, "dd.MM.yyyy", { locale: tr }),
    fullFormattedDate: format(date, "d MMMM EEEE HH.mm", { locale: tr }),
    fullFormattedDateShort: format(date, "dd.MM.yyyy HH.mm", { locale: tr }),
    day: format(date, "d", { locale: tr }),
    dayWithZero: format(date, "dd", { locale: tr }),
    month: format(date, "MMMM", { locale: tr }),
    monthWithZero: format(date, "MM", { locale: tr }),
    dayOfWeekShort: format(date, "EE", { locale: tr }),
    dayOfWeek: format(date, "EEEE", { locale: tr }),
    hours: format(date, "HH.mm", { locale: tr }),
    hasPassed: isPast(date),
    isToday: isToday(date),
  };
}

// old query

const API_BASE =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.MODE === "development"
    ? "http://localhost:3001"
    : "https://kaizu-api-8cd10af40cb3.herokuapp.com");

export const myAxios = axios.create({ baseURL: API_BASE });

export const myAxiosAdmin = axios.create({ baseURL: API_BASE });

// Her isteğe token ekle (öğrenci endpoint'leri)
myAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(lsUserKey);
    if (token) {
      config.headers["Authorization"] = `${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor kullanarak her isteğe token ekleyin (admin endpoint'leri)
myAxiosAdmin.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(lsUserKey);
    if (token) {
      config.headers["Authorization"] = `${token}`;
    }
    return config;
  },
  (error) => {
    // Hatalı istek oluşturulma durumunu yakalayın
    return Promise.reject(error);
  }
);

// 401 → oturum geçersiz ya da hesap deaktif (backend deaktif hesabın token'ını
// da reddeder): token'ı sil, login'e at. Tam sayfa yönlendirme; context,
// react-query cache'i ve socket da sıfırlanmış olur.
let isLoggingOut = false;
const logoutOnUnauthorized = (error: any) => {
  const token = localStorage.getItem(lsUserKey);
  const isLoginRequest = (error?.config?.url || "").includes("/user/login");
  if (error?.response?.status === 401 && token && !isLoginRequest && !isLoggingOut) {
    isLoggingOut = true;
    localStorage.removeItem(lsUserKey);
    if (isDeactivatedError(error)) {
      let email = "";
      try {
        email = jwtDecode<{ email?: string }>(token).email || "";
      } catch {
        // bozuk token — email'siz göster
      }
      setDeactivatedEmail(email);
    }
    window.location.assign("/giris-yap");
  }
  return Promise.reject(error);
};
myAxios.interceptors.response.use((response) => response, logoutOnUnauthorized);
myAxiosAdmin.interceptors.response.use((response) => response, logoutOnUnauthorized);

// Request interceptor kullanarak her isteğe token ekleyin
/*
myAxios.interceptors.request.use(
  (config) => {
    const token =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MzAxODM5LCJ1c2VybmFtZSI6Ik9jYWsgVGFucsSxdmVyZGkiLCJlbWFpbCI6Im9tZXItMDEyNUB3aXQuY29tIiwicm9sZSI6InN0dWRlbnQiLCJjb2hvcnRfaWQiOjI5MiwiY29ob3J0X25hbWUiOiJGU1dFQjAxMjUiLCJlZHVjYXRpb25fdHlwZSI6IkZVTExTVEFDSy1GVUxMIiwic3BlY2lhbCI6ImJvdCIsInN0YXR1cyI6MSwibmV3X2NvaG9ydHMiOm51bGwsImV4cCI6MTc0NTY2ODc4NCwiaWF0IjoxNzQ1NTcxNTg0fQ.TFIvC_F3Qzht-3jH-NH5g0ykvVEwkXoYwEbQJO6NqR0";
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // Hatalı istek oluşturulma durumunu yakalayın
    return Promise.reject(error);
  }
);
 */

export const removeTags = (str: string) => str.replace(/(<([^>]+)>)/gi, "");

function decodeHtmlEntities(str: string): string {
  return str
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#60;/g, "<")
    .replace(/&#62;/g, ">");
}

function detectLang(code: string): string {
  if (/<\/?[a-z][\s\S]*>/i.test(code)) return "html";
  if (/def |import |print\(/.test(code)) return "python";
  if (/class\s+\w+\s*{|public\s+static\s+void/.test(code)) return "java";
  if (/SELECT|INSERT|FROM|WHERE/i.test(code)) return "sql";
  return "javascript";
}

export async function highlightHtmlWithShiki(html: string): Promise<string> {
  const preRegex = /<pre[^>]*>([\s\S]*?)<\/pre>/i;
  const match = html.match(preRegex);

  if (!match) return html;

  const code = decodeHtmlEntities(match[1].trim());
  const lang = detectLang(code);

  const highlighted = await codeToHtml(code, {
    lang,
    theme: "ayu-dark",
  });

  return html.replace(preRegex, highlighted);
}

export async function highlighWithShiki(html: string): Promise<string> {
  const highlighted = await codeToHtml(html.trim(), {
    lang: "javascript",
    theme: "ayu-dark",
  });

  return highlighted;
}

const nameShortener = (username: string) => {
  let str = username.trim();
  str = str.replace(/\s+/g, " ");

  const nameArr = str.trim().split(" ");
  const fixed = nameArr.map(
    (s) => s[0].toLocaleUpperCase("tr") + s.slice(1).toLocaleLowerCase("tr")
  );
  return fixed.length > 2 ? `${fixed[0]} ${fixed[1]}` : fixed[0];
};

export function getDataFromLs(key: string) {
  const rawData = localStorage.getItem(key);
  return rawData ? rawData : null;
}

export function convertTokenToUser(token: string) {
  localStorage.setItem(lsUserKey, token);
  const userRaw: IUser = jwtDecode(token);

  const user = { ...userRaw };
  user.shortName = nameShortener(user.username);

  return user;
}

export async function getValidUserFromJWT(
  viewAsToken?: string
): Promise<IUser | null> {
  if (viewAsToken) {
    const viewAsUser: IUser = jwtDecode(viewAsToken);
    if (viewAsUser && isFuture(new Date((viewAsUser as any)?.exp * 1000))) {
      localStorage.setItem(lsUserKey, viewAsToken);
    }
  }
  const token = getDataFromLs(lsUserKey);
  if (!token) return null;

  const user: IUser = jwtDecode(token);

  // serverdan gelmiyor bu, o yüzden tokenda yok
  if (!user.shortName) {
    user.shortName = nameShortener(user.username);
  }

  const exp = new Date((user as any).exp * 1000);

  if (isPast(exp)) {
    localStorage.removeItem(lsUserKey);
    return null;
  }

  if (isPast(sub(exp, { hours: 3 }))) {
    console.log("jwt gonna expire soon, getting a new one");
    try {
      const newToken = await refreshTokenFn({ token });
      const refreshedUser = convertTokenToUser(newToken);

      if (refreshedUser) {
        localStorage.setItem(lsUserKey, newToken);
        refreshedUser.shortName = nameShortener(refreshedUser.username);
        return refreshedUser;
      }
    } catch (err) {
      console.error("Refresh failed in getValidUserFromJWT", err);
      // Token henuz gecerli (yukarida isPast(exp) ile kontrol edildi).
      // Refresh gecici bir hatayla (Heroku cold start, timeout, ag) dustuyse
      // kullaniciyi atma — mevcut gecerli token'la devam et. Token gercekten
      // suresi dolunca zaten ustteki blokta temizlenip null donuyor.
      return user;
    }
  }
  return user;
}

export function determineLlm(wrongCount: number) {
  let result = "gpt-4";
  if (wrongCount === 0 || wrongCount === 2) {
    result = "gpt-4o";
  } else if (wrongCount === 1) {
    result = "gpt-4o"; // anthropic şimdilik devre dışı, çok konuşuyor
  }
  return result;
}

export const prepareUiHistory = (
  aiHistory: IAiDialogHistoryItem[]
): IPracticeHistoryItem[] => {
  const result: IPracticeHistoryItem[] = [];
  if (aiHistory.length <= 2) return result;
  const loopCount = Math.floor((aiHistory.length - 2) / 2);

  for (let i = 0; i < loopCount; i++) {
    const newItem: IPracticeHistoryItem = {
      user_answer: aiHistory[2 * i + 2].content,
      answer: aiHistory[2 * i + 3].content,
      score: aiHistory[2 * i + 3].score,
    };
    result.unshift(newItem);
  }
  return result;
};

/*  */
export function filterFilesByKeys(
  files: Record<string, string>,
  filesToSave: string[]
) {
  return Object.fromEntries(
    Object.entries(files).filter(([key]) => filesToSave.includes(key))
  );
}

export function getCustomWeekRange(date = new Date()) {
  // Haftanın başlangıcını Pazartesi 03:00 olarak ayarla (öğrenciler bazen
  // sabah erken içeride oluyor — geçişin smoothla­ması için 06:00'dan
  // 03:00'a indirildi). Demo cron'u Pazartesi 06:00'da çalışıp planning_date'i
  // 03:00'a yazıyor — bu da bu hafta filter'ı geçirsin diye.
  const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // 1 = Monday
  let customStart = setSeconds(setMinutes(setHours(weekStart, 3), 0), 0);

  if (isBefore(date, customStart)) {
    customStart = addDays(customStart, -7);
  }

  const customEnd = addDays(customStart, 7); // Sonraki Pazartesi 03:00
  return {
    start: format(customStart, "yyyy-MM-dd HH:mm:ss"),
    end: format(customEnd, "yyyy-MM-dd HH:mm:ss"),
  };
}

export function sanitizeUserInput(input: string) {
  return sanitizeHtml(input, {
    allowedTags: ["b", "i", "u", "strong", "em", "code", "span", "pre"],
    allowedAttributes: {
      span: ["class"],
    },
    allowedClasses: {
      span: ["code", "tWarning"],
    },
  });
}

export function transformSandpackAnswer(
  wantedFileNames: string,
  files: SandpackFiles
) {
  const userAnswerObj: Record<string, string> = {};
  wantedFileNames.split("|").forEach((element) => {
    userAnswerObj[element] =
      typeof files[element] === "string"
        ? files[element]
        : (files[element] as { code: string }).code;
  });
  return { answer: JSON.stringify(userAnswerObj) };
}
